import React, { useEffect, useMemo, useState } from "react";
import { onValue, ref } from "firebase/database";
import { CSVLink } from "react-csv";
import {
  FaArrowDown,
  FaArrowUp,
  FaChartLine,
  FaDownload,
  FaFilter,
  FaRupeeSign,
} from "react-icons/fa";
import { FiClock, FiCheck, FiTruck, FiPackage, FiX } from "react-icons/fi";
import { db } from "../firebase/config";

// Small helpers and config used across the report
const RANGE_OPTIONS = [
  { id: "7d", label: "Last 7 days" },
  { id: "30d", label: "30 days" },
  { id: "90d", label: "90 days" },
  { id: "ytd", label: "YTD" },
  { id: "all", label: "All time" },
  { id: "custom", label: "Custom" },
];

const currency = (value = 0, maximumFractionDigits = 0) =>
  `₹${Number(value || 0).toLocaleString("en-IN", {
    maximumFractionDigits,
    minimumFractionDigits: 0,
  })}`;

const safeFixed = (value, decimals = 2) => {
  const num = Number(value);
  return isNaN(num) || !isFinite(num) ? "0.00" : num.toFixed(decimals);
};

const formatDate = (dateString) => {
  if (!dateString) return "-";
  const date = new Date(dateString);
  if (isNaN(date.getTime())) return dateString || "-";
  return date.toLocaleDateString("en-IN", {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
};

const EXPENSE_CONFIG = {
  COGS_PERCENTAGE: 45,
  SHIPPING_PERCENTAGE: 3.3,
  PAYMENT_GATEWAY_FEE: 2.0,
  PACKAGING_PERCENTAGE: 1.3,
  ADMIN_PERCENTAGE: 3.4,
};

const defaultAnalytics = {
  totalRevenue: 0,
  totalCommission: 0,
  totalCost: 0,
  totalShipping: 0,
  totalPaymentFees: 0,
  totalPackaging: 0,
  totalAdmin: 0,
  totalOtherExpenses: 0,
  grossProfit: 0,
  netProfit: 0,
  profitMargin: 0,
  grossMargin: 0,
  ordersCount: 0,
  averageOrderValue: 0,
  productBreakdown: {},
  commissionByRole: {},
  recentSales: [],
  profitProducts: 0,
  lossProducts: 0,
};

const Reports = () => {
  const [salesDetails, setSalesDetails] = useState([]);
  const [commissionDetails, setCommissionDetails] = useState([]); // Added for commission calculation
  const [products, setProducts] = useState({});
  const [salesLoading, setSalesLoading] = useState(true);
  const [productLoading, setProductLoading] = useState(true);
  const [rangePreset, setRangePreset] = useState("30d");
  const [customRange, setCustomRange] = useState({ start: "", end: "" });
  const [statusFilter, setStatusFilter] = useState("all");
  const [searchTerm, setSearchTerm] = useState("");
  const [trainingCollectedAmount, setTrainingCollectedAmount] = useState(0);
  const [trainingLoading, setTrainingLoading] = useState(true);
  const [trainingParticipants, setTrainingParticipants] = useState([]);
  const [companyOrders, setCompanyOrders] = useState([]);
  const [recentOrderFilter, setRecentOrderFilter] = useState("all");
  const [recentSearch, setRecentSearch] = useState("");
  const [trainingEntries, setTrainingEntries] = useState([]);
  const [paymentEntries, setPaymentEntries] = useState([]);
  const [purchaseEntries, setPurchaseEntries] = useState([]);

  const getStatusClass = (status) => {
    const s = (status || "").toString().toLowerCase();
    switch (s) {
      case "pending":
        return "pending";
      case "confirmed":
        return "confirmed";
      case "dispatched":
        return "dispatched";
      case "delivered":
        return "delivered";
      case "cancelled":
        return "cancelled";
      default:
        return "";
    }
  };

  const getStatusIcon = (status) => {
    const s = (status || "").toString().toLowerCase();
    switch (s) {
      case "pending":
        return <FiClock size={12} style={{ marginRight: 6 }} />;
      case "confirmed":
        return <FiCheck size={12} style={{ marginRight: 6 }} />;
      case "dispatched":
        return <FiTruck size={12} style={{ marginRight: 6 }} />;
      case "delivered":
        return <FiPackage size={12} style={{ marginRight: 6 }} />;
      case "cancelled":
        return <FiX size={12} style={{ marginRight: 6 }} />;
      default:
        return null;
    }
  };

  useEffect(() => {
    const salesRef = ref(db, "HTAMS/sales");
    const commissionRef = ref(db, "HTAMS/salesDetails"); // Fetch sales details for commission
    const unsubscribeSales = onValue(
      salesRef,
      (snapshot) => {
        const data = snapshot.val() || {};
        const parsed = Object.entries(data).map(([id, sale]) => ({
          id,
          ...sale,
        }));
        parsed.sort((a, b) => (b.timestamp || 0) - (a.timestamp || 0));
        setSalesDetails(parsed);
        setSalesLoading(false);
      },
      () => setSalesLoading(false)
    );

    const unsubscribeCommission = onValue(
      commissionRef,
      (snapshot) => {
        const data = snapshot.val() || {};
        const parsed = Object.entries(data).map(([id, sale]) => ({
          id,
          ...sale,
        }));
        setCommissionDetails(parsed);
      },
      () => { }
    );

    const productsRef = ref(db, "HTAMS/company/products");
    const unsubscribeProducts = onValue(
      productsRef,
      (snapshot) => {
        setProducts(snapshot.val() || {});
        setProductLoading(false);
      },
      () => setProductLoading(false)
    );
    const ordersRef = ref(db, "HTAMS/orders/");
    const unsubscribeCompanyOrders = onValue(
      ordersRef,
      (snapshot) => {
        const data = snapshot.val() || {};
        const parsed = Object.entries(data).map(([id, order]) => ({
          id,
          ...order,
        }));
        setCompanyOrders(parsed);
        try {
          const delivered = parsed.filter((o) => {
            const s = (o.status || o.orderStatus || "")
              .toString()
              .toLowerCase();
            return s.includes("deliv");
          });
          const sample = delivered
            .slice(0, 3)
            .map((d) => ({
              id: d.id,
              status: d.status,
              totalAmount: d.totalAmount || d.amount || d.price,
            }));
          console.log(
            "CompanyOrders fetched:",
            parsed.length,
            "delivered count:",
            delivered.length,
            "sample delivered:",
            sample
          );
        } catch (e) {
          console.warn("Error logging company orders", e);
        }
      },
      () => { }
    );
    const trainingRef = ref(db, "HTAMS/company/trainings");
    const trainingEntriesRef = ref(db, "HTAMS/expenses/trainingentry");

    const unsubscribeTrainingEntries = onValue(
      trainingEntriesRef,
      (snapshot) => {
        const data = snapshot.val() || {};
        const parsed = Object.entries(data).map(([id, entry]) => ({
          id,
          ...entry,
        }));
        // Sort by date desc
        parsed.sort((a, b) => {
          const dateA = new Date(a.date || a.createdAt || 0);
          const dateB = new Date(b.date || b.createdAt || 0);
          return dateB - dateA;
        });
        setTrainingEntries(parsed);
        console.log("Training entries fetched:", parsed.length);
      },
      () => { }
    );

    const paymentEntriesRef = ref(db, "HTAMS/expenses/paymententry");
    const unsubscribePaymentEntries = onValue(
      paymentEntriesRef,
      (snapshot) => {
        const data = snapshot.val() || {};
        const parsed = Object.entries(data).map(([id, entry]) => ({
          id,
          ...entry,
        }));
        // Sort by date desc
        parsed.sort((a, b) => {
          const dateA = new Date(a.date || a.createdAt || 0);
          const dateB = new Date(b.date || b.createdAt || 0);
          return dateB - dateA;
        });
        setPaymentEntries(parsed);
        console.log("Payment entries fetched:", parsed.length);
      },
      () => { }
    );

    const purchasesRef = ref(db, "HTAMS/expenses/purchaseentry");
    const unsubscribePurchases = onValue(
      purchasesRef,
      (snapshot) => {
        const data = snapshot.val() || {};
        const parsed = Object.entries(data).map(([id, purchase]) => ({
          id,
          ...purchase,
        }));
        setPurchaseEntries(parsed);
        console.log("Purchase entries fetched:", parsed.length);
      },
      () => { }
    );

    const unsubscribeTrainings = onValue(
      trainingRef,
      (snapshot) => {
        const trainings = snapshot.val() || {};
        let totalTrainingAmount = 0;
        const participantsList = [];

        console.log("\n\n🔥🔥🔥 === RAW FIREBASE SNAPSHOT === 🔥🔥🔥");
        console.log("Snapshot exists:", snapshot.exists());
        console.log("Snapshot val():", snapshot.val());
        console.log("Trainings object:", trainings);
        const trainingKeys = Object.keys(trainings);
        console.log("🔑 Training Keys Array:", trainingKeys);
        console.log("📊 Total trainings in database:", trainingKeys.length);
        console.log("\n=== PROCESSING EACH TRAINING ===");

        let processedCount = 0;
        Object.entries(trainings).forEach(([trainingId, training], index) => {
          console.log(`\n--- 🎯 Training ${index + 1}/${trainingKeys.length} ---`);
          console.log("Training ID:", trainingId);
          console.log("Training data:", training);

          const participants = training.participants || {};
          const trainingName =
            training.trainingName ||
            training.name ||
            (training.location ? `${training.location} Training` : "") ||
            (training.venue ? `${training.venue} Training` : "Unknown Training");

          console.log(`✅ Training Name: ${trainingName}`);
          console.log(`👥 Participants:`, participants);
          console.log(`👥 Participants count: ${Object.keys(participants).length}`);
          processedCount++;

          Object.entries(participants).forEach(
            ([participantId, p]) => {
              // Comprehensive payment field checking
              const paid =
                Number(
                  p.feePaid ||
                  p.fee_paid ||
                  p.paymentDetails?.amount_paid ||
                  p.paymentDetails?.amountPaid ||
                  p.paymentDetails?.amount ||
                  p.amountPaid ||
                  p.amount_paid ||
                  p.amount ||
                  p.totalPaid ||
                  p.total_paid ||
                  0
                ) || 0;

              const participantName =
                p.name ||
                p.participantName ||
                p.participant_name ||
                p.userName ||
                p.user_name ||
                "Unknown";

              const paymentDate =
                p.paymentDate ||
                p.payment_date ||
                p.date ||
                p.joinDate ||
                p.join_date ||
                p.registrationDate ||
                training.startDate ||
                training.start_date ||
                training.date ||
                training.createdAt ||
                training.created_at;

              console.log(`    - ${participantName}: ₹${paid}`);

              totalTrainingAmount += paid;

              // Add ALL participants to the list (removed the paid > 0 condition)
              // This ensures all training participants are displayed
              participantsList.push({
                id: `${trainingId}_${participantId}`, // Unique ID combining training and participant
                participantId: participantId,
                trainingId: trainingId,
                name: participantName,
                amount: paid,
                trainingName: trainingName,
                date: paymentDate,
              });
            }
          );
        });

        console.log("\n\n🎉 === TRAINING DATA SUMMARY === 🎉");
        console.log("✅ Trainings in Firebase:", trainingKeys.length);
        console.log("✅ Trainings processed:", processedCount);
        console.log("✅ Total participants found:", participantsList.length);
        console.log("💰 Total amount collected: ₹", totalTrainingAmount);
        console.log("📋 Full participants list:", participantsList);
        console.log("================================\n\n");

        setTrainingParticipants(participantsList);
        setTrainingLoading(false);
      },
      () => setTrainingLoading(false)
    );

    return () => {
      unsubscribeSales();
      unsubscribeProducts();
      unsubscribeTrainings();
      unsubscribeCompanyOrders();
      unsubscribeTrainings();
      unsubscribeCompanyOrders();
      unsubscribePurchases();
      unsubscribeTrainingEntries();
      unsubscribePaymentEntries();
    };
  }, []);

  const statusOptions = useMemo(() => {
    const statuses = new Set();
    salesDetails.forEach((sale) => {
      if (sale.status) {
        statuses.add(sale.status.toLowerCase());
      }
    });
    return Array.from(statuses);
  }, [salesDetails]);

  const filteredSales = useMemo(() => {
    const now = new Date();
    let startDate = null;
    let endDate = new Date(now);

    if (rangePreset === "custom" && customRange.start && customRange.end) {
      startDate = new Date(customRange.start);
      endDate = new Date(customRange.end);
      endDate.setHours(23, 59, 59, 999);
    } else if (
      rangePreset === "7d" ||
      rangePreset === "30d" ||
      rangePreset === "90d"
    ) {
      const days = parseInt(rangePreset, 10);
      startDate = new Date(now);
      startDate.setDate(now.getDate() - days);
    } else if (rangePreset === "ytd") {
      startDate = new Date(now.getFullYear(), 0, 1);
    } else {
      startDate = null;
    }

    return salesDetails.filter((sale) => {
      const saleDate = sale.saleDate
        ? new Date(sale.saleDate)
        : sale.timestamp
          ? new Date(sale.timestamp)
          : null;
      if (!saleDate) {
        return false;
      }
      if (startDate && saleDate < startDate) {
        return false;
      }
      if (rangePreset !== "all" && saleDate > endDate) {
        return false;
      }
      if (statusFilter !== "all") {
        const saleStatus = (sale.status || "unknown").toLowerCase();
        if (saleStatus !== statusFilter) {
          return false;
        }
      }
      if (searchTerm.trim()) {
        const tokens = searchTerm.toLowerCase();
        const haystack = [
          sale.product?.name,
          sale.productName,
          sale.customerName,
          sale.orderId,
          sale.id,
        ]
          .filter(Boolean)
          .join(" ")
          .toLowerCase();
        if (!haystack.includes(tokens)) {
          return false;
        }
      }
      return true;
    });
  }, [salesDetails, rangePreset, customRange, statusFilter, searchTerm]);

  const analytics = useMemo(() => {
    const now = new Date();
    let aStartDate = null;
    let aEndDate = new Date(now);

    if (rangePreset === "custom" && customRange.start && customRange.end) {
      aStartDate = new Date(customRange.start);
      aEndDate = new Date(customRange.end);
      aEndDate.setHours(23, 59, 59, 999);
    } else if (rangePreset === "7d") {
      aStartDate = new Date(now);
      aStartDate.setDate(now.getDate() - 7);
    } else if (rangePreset === "30d") {
      aStartDate = new Date(now);
      aStartDate.setDate(now.getDate() - 30);
    } else if (rangePreset === "90d") {
      aStartDate = new Date(now);
      aStartDate.setDate(now.getDate() - 90);
    } else if (rangePreset === "ytd") {
      aStartDate = new Date(now.getFullYear(), 0, 1);
    } else {
      aStartDate = null;
    }

    const isDateInCheck = (d) => {
      if (!d) return false;
      const dateObj = new Date(d);
      if (isNaN(dateObj.getTime())) return false;
      if (aStartDate && dateObj < aStartDate) return false;
      if (rangePreset !== "all" && dateObj > aEndDate) return false;
      return true;
    };

    const isStatusMatch = (status) => {
      if (statusFilter === "all") return true;
      return (status || "").toLowerCase() === statusFilter.toLowerCase();
    };

    const isSearchMatch = (textCandidates) => {
      if (!searchTerm.trim()) return true;
      const q = searchTerm.toLowerCase();
      const joint = textCandidates.filter(Boolean).join(" ").toLowerCase();
      return joint.includes(q);
    };

    // 1. Training Participants
    const filteredTrainingParticipants = trainingParticipants.filter((p) => {
      if (!isDateInCheck(p.date)) return false;
      // Assuming simplified search/status for training
      if (!isSearchMatch([p.name, p.trainingName])) return false;
      return true;
    });
    const trainingRevenue = filteredTrainingParticipants.reduce(
      (sum, p) => sum + (Number(p.amount) || 0),
      0
    );

    // 2. Company Orders
    const calculateOrderAmount = (order) => {
      const toNum = (v) => { const n = Number(v); return isNaN(n) ? 0 : n; };
      let val = toNum(order.totalAmount || order.amount || order.price || order.paidAmount || order.paymentAmount);
      if (val <= 0 && order.paymentDetails) val = toNum(order.paymentDetails.amount_paid || order.paymentDetails.amount);
      if (val <= 0 && Array.isArray(order.items)) val = order.items.reduce((s, i) => s + (toNum(i.total) || toNum(i.price) * toNum(i.quantity)), 0);
      if (val <= 0 && order.quantity && order.unitPrice) val = toNum(order.quantity) * toNum(order.unitPrice);
      return val;
    };

    const filteredOrders = companyOrders.filter((order) => {
      const d = order.orderDate || order.createdAt || order.timestamp;
      if (!isDateInCheck(d)) return false;

      const status = order.status || order.orderStatus || "pending";
      if (!isStatusMatch(status)) return false;

      const searchFields = [order.id, order.customerName, order.userName, order.productName];
      if (order.items) searchFields.push(...order.items.map(i => i.name || i.productName));

      if (!isSearchMatch(searchFields)) return false;
      return true;
    });

    const orderRevenue = filteredOrders.reduce((sum, o) => sum + calculateOrderAmount(o), 0);

    const deliveredOrdersSubset = filteredOrders.filter(o => (o.status || "").toLowerCase().includes("deliver"));
    const deliveredOrderRevenue = deliveredOrdersSubset.reduce((sum, o) => sum + calculateOrderAmount(o), 0);
    const deliveredOrdersCount = deliveredOrdersSubset.length;

    // 3. Purchase Entries
    const filteredPurchases = purchaseEntries.filter(p => {
      const d = p.date || p.createdAt || p.timestamp;
      if (!isDateInCheck(d)) return false;
      if (!isSearchMatch([p.supplierName, p.vendor, p.billNumber])) return false;
      return true;
    });
    const totalPurchaseAmount = filteredPurchases.reduce((sum, p) => sum + (Number(p.totalAmount) || Number(p.amount) || 0), 0);

    // 4. Training Expenses
    const filteredTrainingExpenses = trainingEntries.filter(t => {
      const d = t.date || t.createdAt || t.timestamp;
      if (!isDateInCheck(d)) return false;
      if (!isSearchMatch([t.traineeName, t.description])) return false;
      return true;
    });
    const trainingBreakdown = filteredTrainingExpenses.reduce((acc, t) => {
      acc.trainingCost += (Number(t.trainingCost) || 0);
      acc.trainerFee += (Number(t.trainerFee) || 0);
      acc.materialCost += (Number(t.materialCost) || 0);
      acc.venueCost += (Number(t.venueCost) || 0);
      acc.otherExpenses += (Number(t.otherExpenses) || 0);
      acc.gstAmount += (Number(t.gstAmount) || 0);
      acc.total += (Number(t.totalCost) || Number(t.amount) || 0) + (Number(t.gstAmount) || 0);
      return acc;
    }, { trainingCost: 0, trainerFee: 0, materialCost: 0, venueCost: 0, otherExpenses: 0, gstAmount: 0, total: 0 });
    const totalTrainingEntriesAmount = trainingBreakdown.total;

    // 5. Payment Entries
    const filteredPaymentEntries = paymentEntries.filter(p => {
      const d = p.date || p.createdAt || p.timestamp;
      if (!isDateInCheck(d)) return false;
      if (!isSearchMatch([p.partyName, p.expenseType])) return false;
      return true;
    });
    const totalPaymentEntriesAmount = filteredPaymentEntries.reduce((sum, p) => sum + (Number(p.amount) || 0), 0);

    // Aggregates
    // User requested Total Revenue to only include DELIVERED items amount
    const totalRevenue = trainingRevenue + deliveredOrderRevenue;

    // Commission Calculation (from commissionDetails)
    const filteredCommissionSales = commissionDetails.filter(s => isDateInCheck(s.saleDate || s.timestamp || s.createdAt));
    const totalCommission = filteredCommissionSales.reduce((sum, sale) => {
      if (sale.commissionDistributed && sale.commissions) {
        const saleCommission = Object.values(sale.commissions).reduce((cSum, c) => cSum + (Number(c.amount) || 0), 0);
        return sum + saleCommission;
      }
      return sum;
    }, 0);

    const totalCost = 0;
    const totalShipping = 0;
    const totalPaymentFees = 0;
    const totalPackaging = 0;
    const totalAdmin = 0;
    const totalOtherExpenses = 0;

    // Calculated Total Expenses as per user request (Purchases + Training + Payments)
    const totalExpenses = totalPurchaseAmount + totalTrainingEntriesAmount + totalPaymentEntriesAmount;

    // Net Profit
    const netProfit = totalRevenue - totalExpenses - totalCommission;
    const grossProfit = totalRevenue;

    // Recent Sales Table Data
    const recentSales = filteredOrders
      .sort((a, b) => new Date(b.orderDate || b.createdAt || 0) - new Date(a.orderDate || a.createdAt || 0))
      .slice(0, 100)
      .map(o => ({
        id: o.id,
        productName: o.productName || (o.items?.[0]?.name) || "Product",
        customer: o.customerName || o.userName || "Guest",
        saleDate: o.orderDate || o.createdAt,
        revenue: calculateOrderAmount(o),
        status: o.status || "Pending",
        cost: 0, commission: 0, otherExpenses: 0, net: 0
      }));

    return {
      totalRevenue,
      trainingRevenue,
      orderRevenue,
      totalDeliveredOrdersAmount: deliveredOrderRevenue,
      totalDeliveredOrdersCount: deliveredOrdersCount,
      totalPurchaseAmount,
      totalPurchaseCount: filteredPurchases.length,
      purchaseEntriesDetails: filteredPurchases, // Exposed for table
      totalTrainingEntriesAmount,
      totalTrainingEntriesCount: filteredTrainingExpenses.length,
      trainingEntriesDetails: filteredTrainingExpenses,
      trainingBreakdown,
      totalPaymentEntriesAmount,
      totalPaymentEntriesCount: filteredPaymentEntries.length,
      paymentEntriesDetails: filteredPaymentEntries,

      totalCommission, totalCost, totalShipping, totalPaymentFees, totalPackaging, totalAdmin, totalOtherExpenses,
      totalExpenses,
      grossProfit, netProfit,
      profitMargin: totalRevenue > 0 ? (netProfit / totalRevenue) * 100 : 0,
      grossMargin: 100,
      averageOrderValue: filteredOrders.length > 0 ? orderRevenue / filteredOrders.length : 0,
      ordersCount: filteredOrders.length,
      productBreakdown: {},
      commissionByRole: {},
      recentSales,
      profitProducts: 0,
      lossProducts: 0,
      filteredTrainingParticipants,
      filteredCommissionSales // Return this for the new card
    };

  }, [rangePreset, customRange, statusFilter, searchTerm, trainingParticipants, companyOrders, purchaseEntries, trainingEntries, paymentEntries, commissionDetails]);

  const loading = salesLoading || productLoading || trainingLoading;

  const productRows = Object.values(analytics.productBreakdown).sort(
    (a, b) => b.revenue - a.revenue
  );
  const topProfitable = [...productRows]
    .sort((a, b) => b.netProfit - a.netProfit)
    .slice(0, 5);
  const topLoss = [...productRows]
    .filter((p) => p.netProfit < 0)
    .sort((a, b) => a.netProfit - b.netProfit)
    .slice(0, 5);

  const csvData = productRows.map((row) => ({
    productId: row.productId,
    productName: row.productName,
    revenue: row.revenue,
    commission: row.commission,
    cost: row.cost,
    otherExpenses: row.otherExpenses,
    netProfit: row.netProfit,
    quantity: row.quantity,
    unitCost: row.unitCost,
    averageSellingPrice: row.averageSellingPrice,
    grossMargin: row.grossMargin,
    marginPercent: row.marginPercent,
  }));

  const handleRangeChange = (preset) => {
    setRangePreset(preset);
    if (preset !== "custom") {
      setCustomRange({ start: "", end: "" });
    }
  };

  return (
    <div className="reports-page">
      <div className="page-header-card">
        <div className="reports-header">
          <div>
            <p className="breadcrumb">Analytics / Profitability</p>
            <h1>Performance & Profitability Reports</h1>
            <p className="subtitle">
              Track sales, commissions, and profit / loss in one
              place.
            </p>
          </div>

          {/* <div className="header-actions">
                    <CSVLink
                        data={analytics.recentSales} // Fallback to companyOrders via logic/manual construction if needed, but recentSales was original
                        filename={"sales-report.csv"}
                        className="export-btn"
                    >
                        <FaDownload /> Export CSV
                    </CSVLink>
                </div> */}

        </div>


        {loading ? (
          <div className="loading-state">
            <div className="spinner" />
            <p>Loading analytics...</p>
          </div>
        ) : (
          <>
            <div className="filters-card">
              <div className="filter-row">
                <div className="filter-group">
                  <label>Date Range</label>
                  <div className="range-buttons">
                    {RANGE_OPTIONS.map((option) => (
                      <button
                        key={option.id}
                        className={`range-btn ${rangePreset === option.id
                          ? "active"
                          : ""
                          }`}
                        onClick={() =>
                          handleRangeChange(option.id)
                        }
                      >
                        {option.label}
                      </button>
                    ))}
                  </div>
                </div>
                <div className="filter-group compact">
                  <label>Status</label>
                  <div className="select-wrapper">
                    <FaFilter />
                    <select
                      value={statusFilter}
                      onChange={(e) =>
                        setStatusFilter(e.target.value)
                      }
                    >
                      <option value="all">All status</option>
                      {statusOptions.map((status) => (
                        <option key={status} value={status}>
                          {status.charAt(0).toUpperCase() +
                            status.slice(1)}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
                <div className="filter-group compact">
                  <label>Search</label>
                  <input
                    type="text"
                    placeholder="Search product, order or customer"
                    value={searchTerm}
                    onChange={(e) =>
                      setSearchTerm(e.target.value)
                    }
                  />
                </div>
              </div>

              {rangePreset === "custom" && (
                <div className="filter-row">
                  <div className="filter-group compact">
                    <label>Start date</label>
                    <input
                      type="date"
                      value={customRange.start}
                      onChange={(e) =>
                        setCustomRange((prev) => ({
                          ...prev,
                          start: e.target.value,
                        }))
                      }
                    />
                  </div>
                  <div className="filter-group compact">
                    <label>End date</label>
                    <input
                      type="date"
                      value={customRange.end}
                      min={customRange.start}
                      onChange={(e) =>
                        setCustomRange((prev) => ({
                          ...prev,
                          end: e.target.value,
                        }))
                      }
                    />
                  </div>
                </div>
              )}

            </div>

            <div className="metrics-grid">
              <div className="metric-card highlight">
                <div className="metric-icon success">
                  <FaRupeeSign />
                </div>
                <div>
                  <p className="metric-label">
                    Total Revenue
                  </p>
                  <p className="metric-value">
                    {currency(analytics.totalRevenue)}{" "}
                  </p>

                </div>
              </div>
              <div className="metric-card">
                <div className="metric-icon info">
                  <FaArrowDown />
                </div>
                <div>
                  <p className="metric-label">Gross Profit</p>
                  <p className="metric-value">
                    {currency(analytics.grossProfit)}
                  </p>
                  <p className="metric-subvalue">
                    Margin{" "}
                    {safeFixed(analytics.grossMargin, 1)}%
                  </p>
                </div>
              </div>
              <div className="metric-card">
                <div className="metric-icon warning">
                  <FaChartLine />
                </div>
                <div>
                  <p className="metric-label">
                    Total Expenses
                  </p>
                  <p className="metric-value">
                    {currency(analytics.totalExpenses)}
                  </p>
                </div>
              </div>
              <div
                className={`metric-card ${analytics.netProfit >= 0
                  ? "positive"
                  : "negative"
                  }`}
              >
                <div className="metric-icon">
                  {analytics.netProfit >= 0 ? (
                    <FaArrowUp />
                  ) : (
                    <FaArrowDown />
                  )}
                </div>
                <div>
                  <p className="metric-label">Net Profit</p>
                  <p className="metric-value">
                    {currency(analytics.netProfit)}
                  </p>
                  <p className="metric-subvalue">
                    Margin{" "}
                    {safeFixed(analytics.profitMargin, 2)}%
                  </p>
                </div>
              </div>
            </div>

            <div className="expense-breakdown-card" style={{ marginTop: "16px" }}>
              <h3>Operational Summaries</h3>
              <div
                className="expense-grid"
                style={{
                  gridTemplateColumns: "repeat(5, 1fr)",
                }}
              >
                <div className="expense-item">
                  <span className="expense-label">
                    Training Participants
                  </span>
                  <span className="expense-value">
                    {currency(analytics.trainingRevenue || 0)}
                  </span>
                  <span className="expense-percent">
                    Participants {analytics.filteredTrainingParticipants?.length || 0}
                  </span>
                </div>

                <div className="expense-item">
                  <span className="expense-label">
                    Company Orders
                  </span>
                  <span className="expense-value">
                    {currency(
                      analytics.totalDeliveredOrdersAmount ||
                      0
                    )}
                  </span>
                  <span className="expense-percent">
                    Delivered{" "}
                    {analytics.totalDeliveredOrdersCount || 0}
                  </span>
                </div>

                <div className="expense-item">
                  <span className="expense-label">
                    Purchase Entries
                  </span>
                  <span className="expense-value">
                    {currency(
                      analytics.totalPurchaseAmount || 0
                    )}
                  </span>
                  <span className="expense-percent">
                    Entries{" "}
                    {analytics.totalPurchaseCount || 0}
                  </span>
                </div>

                <div className="expense-item">
                  <span className="expense-label">
                    Training Expenses
                  </span>
                  <span className="expense-value">
                    {currency(
                      analytics.totalTrainingEntriesAmount ||
                      0
                    )}
                  </span>
                  <div className="expense-percent" style={{ fontSize: '0.7em', marginTop: '4px', opacity: 0.8 }}>
                    Base: {currency(analytics.trainingBreakdown.total - analytics.trainingBreakdown.gstAmount)}
                    <br />
                    GST: {currency(analytics.trainingBreakdown.gstAmount)}
                  </div>
                  <span className="expense-percent">
                    Entries{" "}
                    {analytics.totalTrainingEntriesCount || 0}
                  </span>
                </div>

                <div className="expense-item">
                  <span className="expense-label">
                    Payment Entries
                  </span>
                  <span className="expense-value">
                    {currency(
                      analytics.totalPaymentEntriesAmount || 0
                    )}
                  </span>
                  <span className="expense-percent">
                    Entries{" "}
                    {analytics.totalPaymentEntriesCount || 0}
                  </span>
                </div>

                <div className="expense-item">
                  <span className="expense-label">
                    Total Commission
                  </span>
                  <span className="expense-value">
                    {currency(
                      analytics.totalCommission || 0
                    )}
                  </span>
                  <span className="expense-percent">
                    Entries{" "}
                    {analytics.filteredCommissionSales?.filter(s => s.commissionDistributed).length || 0}
                  </span>
                </div>
              </div>
            </div>

            {/* <div className="expense-breakdown-card">
                            <h3>Expense Breakdown</h3>
                            <div className="expense-grid">
                                <div className="expense-item">
                                    <span className="expense-label">
                                        Cost of Goods (COGS)
                                    </span>
                                    <span className="expense-value">
                                        {currency(analytics.totalCost)}
                                    </span>
                                    <span className="expense-percent">
                                        {safeFixed(
                                            (analytics.totalCost /
                                                (analytics.totalRevenue || 1)) *
                                                100,
                                            1
                                        )}
                                        %
                                    </span>
                                </div>
                                <div className="expense-item">
                                    <span className="expense-label">
                                        Commissions
                                    </span>
                                    <span className="expense-value">
                                        {currency(analytics.totalCommission)}
                                    </span>
                                    <span className="expense-percent">
                                        {safeFixed(
                                            (analytics.totalCommission /
                                                analytics.totalRevenue) *
                                                100,
                                            1
                                        )}
                                        %
                                    </span>
                                </div>
                                <div className="expense-item">
                                    <span className="expense-label">
                                        Shipping & Logistics
                                    </span>
                                    <span className="expense-value">
                                        {currency(analytics.totalShipping)}
                                    </span>
                                    <span className="expense-percent">
                                        {safeFixed(
                                            EXPENSE_CONFIG.SHIPPING_PERCENTAGE,
                                            1
                                        )}
                                        %
                                    </span>
                                </div>
                                <div className="expense-item">
                                    <span className="expense-label">
                                        Payment Gateway
                                    </span>
                                    <span className="expense-value">
                                        {currency(analytics.totalPaymentFees)}
                                    </span>
                                    <span className="expense-percent">
                                        {safeFixed(
                                            EXPENSE_CONFIG.PAYMENT_GATEWAY_FEE,
                                            1
                                        )}
                                        %
                                    </span>
                                </div>
                                <div className="expense-item">
                                    <span className="expense-label">
                                        Packaging
                                    </span>
                                    <span className="expense-value">
                                        {currency(analytics.totalPackaging)}
                                    </span>
                                    <span className="expense-percent">
                                        {safeFixed(
                                            EXPENSE_CONFIG.PACKAGING_PERCENTAGE,
                                            1
                                        )}
                                        %
                                    </span>
                                </div>
                                <div className="expense-item">
                                    <span className="expense-label">
                                        Administrative
                                    </span>
                                    <span className="expense-value">
                                        {currency(analytics.totalAdmin)}
                                    </span>
                                    <span className="expense-percent">
                                        {safeFixed(
                                            EXPENSE_CONFIG.ADMIN_PERCENTAGE,
                                            1
                                        )}
                                        %
                                    </span>
                                </div>
                            </div>
                        </div> */}

            <div className="insights-grid">
              <div className="insight-card">
                <h3>Profit vs Loss</h3>
                <div className="progress">
                  <div
                    className="progress-fill"
                    style={{
                      width: `${analytics.profitProducts +
                        analytics.lossProducts ===
                        0
                        ? 0
                        : (analytics.profitProducts /
                          (analytics.profitProducts +
                            analytics.lossProducts)) *
                        100
                        }%`,
                    }}
                  />
                </div>
                <div className="progress-labels">
                  <span className="positive">
                    {analytics.profitProducts} Profitable
                    products
                  </span>
                  <span className="negative">
                    {analytics.lossProducts} Loss-making
                    products
                  </span>
                </div>
              </div>

              <div className="insight-card">
                <h3>Commission distribution</h3>
                {(!analytics.filteredCommissionSales || analytics.filteredCommissionSales.filter(s => s.commissionDistributed).length === 0) ? (
                  <p className="muted-text">
                    No commission data available.
                  </p>
                ) : (
                  <div className="commission-list-scroll" style={{ maxHeight: '200px', overflowY: 'auto' }}>
                    <ul className="pill-list">
                      {analytics.filteredCommissionSales
                        .filter(sale => sale.commissionDistributed && sale.commissions)
                        .sort((a, b) => {
                          const dateA = new Date(a.saleDate || a.timestamp || a.createdAt || 0);
                          const dateB = new Date(b.saleDate || b.timestamp || b.createdAt || 0);
                          return dateB - dateA;
                        })
                        .slice(0, 10) // Limit to top 10 for this small card
                        .map((sale) => {
                          const totalComm = Object.values(sale.commissions || {}).reduce((sum, c) => sum + (Number(c.amount) || 0), 0);
                          return (
                            <li key={sale.id} style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                              <span className="role" style={{ fontSize: '0.9em' }}>
                                {(sale.orderId || sale.id).length > 15 ? (sale.orderId || sale.id).substring(0, 15) + '...' : (sale.orderId || sale.id)}
                              </span>
                              <span className="amount" style={{ fontWeight: 'bold', color: '#48bb78' }}>
                                {currency(totalComm)}
                              </span>
                            </li>
                          );
                        })}
                    </ul>
                  </div>
                )}
              </div>



              <div className="table-card">
                <div className="table-card-header">
                  <h3>Top profitable products</h3>
                  <span className="badge positive">
                    {topProfitable.length}
                  </span>
                </div>
                {topProfitable.length === 0 ? (
                  <p className="muted-text">
                    No profitable products in the selected
                    range.
                  </p>
                ) : (
                  <div className="training-table-scroll">
                    <table>
                      <thead>
                        <tr>
                          <th>Product</th>
                          <th>Revenue</th>
                          <th>Net Profit</th>
                          <th>Margin</th>
                        </tr>
                      </thead>
                      <tbody>
                        {topProfitable.map(
                          (product) => (
                            <tr
                              key={
                                product.productId
                              }
                            >
                              <td>
                                {
                                  product.productName
                                }
                              </td>
                              <td>
                                {currency(
                                  product.revenue
                                )}
                              </td>
                              <td className="positive">
                                {currency(
                                  product.netProfit
                                )}
                              </td>
                              <td>
                                {safeFixed(
                                  product.marginPercent,
                                  1
                                )}
                                %
                              </td>
                            </tr>
                          )
                        )}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>

              <div className="table-card">
                <div className="table-card-header">
                  <h3>Loss-making products</h3>
                  <span className="badge negative">
                    {topLoss.length}
                  </span>
                </div>
                {topLoss.length === 0 ? (
                  <p className="muted-text">
                    No loss-making products in this range 👏
                  </p>
                ) : (
                  <table>
                    <thead>
                      <tr>
                        <th>Product</th>
                        <th>Revenue</th>
                        <th>Net Loss</th>
                        <th>Margin</th>
                      </tr>
                    </thead>
                    <tbody>
                      {topLoss.map((product) => (
                        <tr key={product.productId}>
                          <td>
                            {product.productName}
                          </td>
                          <td>
                            {currency(
                              product.revenue
                            )}
                          </td>
                          <td className="negative">
                            {currency(
                              product.netProfit
                            )}
                          </td>
                          <td>
                            {safeFixed(
                              product.marginPercent,
                              1
                            )}
                            %
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            </div>

            <div className="tables-grid">
              <div className="table-card training-card-compact">
                <div className="table-card-header">
                  <h3>Training Participants Payment</h3>
                  <span className="badge info">
                    {analytics.filteredTrainingParticipants?.length || 0}
                  </span>
                </div>
                {(!analytics.filteredTrainingParticipants || analytics.filteredTrainingParticipants.length === 0) ? (
                  <p className="muted-text">
                    No training payments in this range
                  </p>
                ) : (
                  <div className="training-table-scroll">
                    <table>
                      <thead>
                        <tr>
                          <th>Participant Name</th>
                          <th>Training Name</th>
                          <th>Amount Paid</th>
                        </tr>
                      </thead>
                      <tbody>
                        {analytics.filteredTrainingParticipants
                          .sort((a, b) => {
                            const dateA = new Date(a.date || 0);
                            const dateB = new Date(b.date || 0);
                            return dateB - dateA; // Most recent first
                          })
                          .map(
                            (participant) => (
                              <tr key={participant.id}>
                                <td>
                                  {participant.name}
                                </td>
                                <td>
                                  {participant.trainingName}
                                </td>
                                <td className={participant.amount > 0 ? "positive" : "muted-text"}>
                                  {currency(
                                    participant.amount
                                  )}
                                  {participant.amount === 0 && <span style={{ fontSize: "0.85em", marginLeft: "5px" }}>(Unpaid)</span>}
                                </td>
                              </tr>
                            )
                          )}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>

              <div className="table-card training-card-compact">
                <div className="table-card-header">
                  <h3>Training Expenses Details</h3>
                  <span className="badge info">
                    {analytics.trainingEntriesDetails?.length || 0}
                  </span>
                </div>
                {(!analytics.trainingEntriesDetails || analytics.trainingEntriesDetails.length === 0) ? (
                  <p className="muted-text">
                    No training entries in this range
                  </p>
                ) : (
                  <div className="training-table-scroll">
                    <table>
                      <thead>
                        <tr>
                          <th>Date</th>
                          <th>Trainee Name</th>
                          <th>Type</th>
                          <th>Trainer Fee</th>
                          <th>Material</th>
                          <th>Venue</th>
                          <th>Other</th>
                          <th>GST</th>
                          <th>Total</th>
                        </tr>
                      </thead>
                      <tbody>
                        {analytics.trainingEntriesDetails.map(
                          (entry) => (
                            <tr key={entry.id}>
                              <td>{formatDate(entry.date)}</td>
                              <td>{entry.traineeName || "Unknown"}</td>
                              <td>{entry.trainingType || "-"}</td>
                              <td>{currency(entry.trainerFee || 0)}</td>
                              <td>{currency(entry.materialCost || 0)}</td>
                              <td>{currency(entry.venueCost || 0)}</td>
                              <td>{currency(entry.otherExpenses || 0)}</td>
                              <td>{currency(entry.gstAmount || 0)}</td>
                              <td className="negative" style={{ fontWeight: 'bold' }}>
                                {currency(
                                  (Number(entry.totalCost) || Number(entry.amount) || 0) + (Number(entry.gstAmount) || 0)
                                )}
                              </td>
                            </tr>
                          )
                        )}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>

              <div className="table-card training-card-compact purchase-card">
                <div className="table-card-header">
                  <h3>Recent Purchase Entries</h3>
                  <span className="badge info">
                    {analytics.purchaseEntriesDetails?.length || 0}
                  </span>
                </div>
                {(!analytics.purchaseEntriesDetails || analytics.purchaseEntriesDetails.length === 0) ? (
                  <p className="muted-text">
                    No purchase entries available
                  </p>
                ) : (
                  <div className="training-table-scroll">
                    <table>
                      <thead>
                        <tr>
                          <th>Supplier</th>
                          <th>Invoice</th>
                          <th>Amount</th>
                          <th>Date</th>
                        </tr>
                      </thead>
                      <tbody>
                        {analytics.purchaseEntriesDetails
                          .slice()
                          .sort((a, b) => {
                            const ta = new Date(
                              a.date ||
                              a.createdAt ||
                              a.timestamp ||
                              0
                            ).getTime();
                            const tb = new Date(
                              b.date ||
                              b.createdAt ||
                              b.timestamp ||
                              0
                            ).getTime();
                            return tb - ta;
                          })
                          .slice(0, 8)
                          .map((p) => (
                            <tr key={p.id}>
                              <td>
                                {p.supplierName ||
                                  (p.supplier &&
                                    (typeof p.supplier ===
                                      "string"
                                      ? p.supplier
                                      : p
                                        .supplier
                                        .name)) ||
                                  p.vendor ||
                                  "Unknown"}
                              </td>
                              <td>
                                {p.billNumber ||
                                  p.invoiceNumber ||
                                  p.voucher ||
                                  p.id}
                              </td>
                              <td className="negative">
                                {currency(
                                  Number(
                                    p.totalAmount
                                  ) ||
                                  Number(
                                    p.total
                                  ) ||
                                  Number(
                                    p.amount
                                  ) ||
                                  0
                                )}
                              </td>
                              <td>
                                {p.date ||
                                  p.createdAt ||
                                  p.timestamp
                                  ? new Date(
                                    p.date ||
                                    p.createdAt ||
                                    p.timestamp
                                  ).toLocaleDateString(
                                    "en-IN",
                                    {
                                      day: "numeric",
                                      month: "short",
                                      year: "numeric"
                                    }
                                  )
                                  : "-"}
                              </td>
                            </tr>
                          ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>

              <div className="table-card training-card-compact purchase-card">
                <div className="table-card-header">
                  <h3>Recent Payment Entries</h3>
                  <span className="badge info">
                    {analytics.paymentEntriesDetails?.length || 0}
                  </span>
                </div>
                {(!analytics.paymentEntriesDetails || analytics.paymentEntriesDetails.length === 0) ? (
                  <p className="muted-text">
                    No payment entries available
                  </p>
                ) : (
                  <div className="training-table-scroll">
                    <table>
                      <thead>
                        <tr>
                          <th>Party Name</th>
                          <th>Expense Type</th>
                          <th>Amount</th>
                        </tr>
                      </thead>
                      <tbody>
                        {analytics.paymentEntriesDetails
                          .map((p) => (
                            <tr key={p.id}>
                              <td>
                                {p.partyName || "Unknown"}
                              </td>
                              <td>
                                {(p.expenseType || "N/A")
                                  .replace(/_/g, " ")
                                  .replace(/\b\w/g, (l) =>
                                    l.toUpperCase()
                                  )}
                              </td>
                              <td className="negative">
                                {currency(
                                  p.amount
                                )}
                              </td>
                            </tr>
                          ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>


            </div>

            <div className="table-card full-width">
              <div className="table-card-header">
                <h3>Recent sales</h3>
                <span className="badge info">
                  {analytics.recentSales.length}
                </span>
              </div>
              <div className="recent-controls">
                <div className="select-wrapper">
                  <FaFilter />
                  <select
                    value={recentOrderFilter}
                    onChange={(e) =>
                      setRecentOrderFilter(e.target.value)
                    }
                  >
                    <option value="all">All Orders</option>
                    <option value="pending">Pending</option>
                    <option value="confirmed">
                      Confirmed
                    </option>
                    <option value="dispatched">
                      Dispatched
                    </option>
                    <option value="delivered">
                      Delivered
                    </option>
                    <option value="cancelled">
                      Cancelled
                    </option>
                  </select>
                </div>
                <input
                  type="text"
                  placeholder="Search product, order or customer"
                  value={recentSearch}
                  onChange={(e) =>
                    setRecentSearch(e.target.value)
                  }
                />
              </div>
              {analytics.recentSales.length === 0 ? (
                <p className="muted-text">
                  No recent sales available.
                </p>
              ) : (
                <div className="recent-table-scroll">
                  <table>
                    <thead>
                      <tr>
                        <th>Product</th>
                        <th>Customer</th>
                        <th>Status</th>
                        <th>Revenue</th>
                        <th>Date</th>
                      </tr>
                    </thead>
                    {(() => {
                      const q = recentSearch
                        .trim()
                        .toLowerCase();
                      const filtered =
                        analytics.recentSales.filter(
                          (sale) => {
                            const status = (
                              sale.status ||
                              sale.orderStatus ||
                              ""
                            )
                              .toString()
                              .toLowerCase();
                            if (
                              recentOrderFilter !==
                              "all"
                            ) {
                              if (
                                status !==
                                recentOrderFilter
                              )
                                return false;
                            }
                            if (q) {
                              const hay = [
                                sale.product
                                  ?.name,
                                sale.productName,
                                sale.customerName,
                                sale.customer,
                                sale.orderId,
                                sale.id,
                              ]
                                .filter(Boolean)
                                .join(" ")
                                .toLowerCase();
                              if (
                                !hay.includes(q)
                              )
                                return false;
                            }
                            return true;
                          }
                        );

                      return (
                        <tbody>
                          {filtered.map((sale) => (
                            <tr key={sale.id}>
                              <td>
                                {
                                  sale.productName
                                }
                              </td>
                              <td>
                                {sale.customer}
                              </td>
                              <td>
                                <span
                                  className={`status-badge ${getStatusClass(
                                    sale.status
                                  )}`}
                                >
                                  {getStatusIcon(
                                    sale.status
                                  )}
                                  {
                                    sale.status
                                  }
                                </span>
                              </td>
                              <td>
                                {currency(
                                  sale.revenue
                                )}
                              </td>
                              <td>
                                {sale.saleDate
                                  ? new Date(
                                    sale.saleDate
                                  ).toLocaleDateString(
                                    "en-IN",
                                    {
                                      day: "numeric",
                                      month: "short",
                                      year: "numeric"
                                    }
                                  )
                                  : "-"}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      );
                    })()}
                  </table>
                </div>
              )}
            </div>
          </>
        )}

        <style jsx>{`
                    .reports-page {
                        padding: 20px;
                        background: #f4f6fb;
                        min-height: 100vh;
                    }
                    .reports-header {
                        display: flex;
                        justify-content: space-between;
                        align-items: flex-start;
                        gap: 16px;
                    }
                    .breadcrumb {
                        font-size: 0.85rem;
                        color: #94a3b8;
                        margin: 0 0 4px 0;
                        letter-spacing: 0.08em;
                        text-transform: uppercase;
                    }
                    .reports-header h1 {
                        margin: 0;
                        font-size: 2rem;
                        font-weight: 700;
                        color: #0f172a;
                    }
                    .subtitle {
                        margin: 6px 0 0 0;
                        color: #64748b;
                    }
                    .header-actions {
                        display: flex;
                        gap: 12px;
                    }
                    .action-btn {
                        display: inline-flex;
                        align-items: center;
                        gap: 8px;
                        border-radius: 10px;
                        border: none;
                        cursor: pointer;
                        font-weight: 600;
                        padding: 10px 18px;
                        text-decoration: none;
                    }
                    .action-btn.primary {
                        background: linear-gradient(135deg, #6366f1, #8b5cf6);
                        color: white;
                        box-shadow: 0 8px 20px rgba(99, 102, 241, 0.25);
                    }
                    .filters-card {
                        margin-top: 20px;
                        padding: 20px;
                        border-radius: 16px;
                        background: white;
                        box-shadow: 0 12px 40px rgba(15, 23, 42, 0.05);
                    }
                    .filter-row {
                        display: flex;
                        flex-wrap: wrap;
                        gap: 20px;
                    }
                    .filter-group {
                        flex: 1;
                        min-width: 220px;
                    }
                    .filter-group.compact {
                        flex: 0 1 220px;
                    }
                    .filter-group label {
                        display: block;
                        margin-bottom: 8px;
                        font-weight: 600;
                        color: #475569;
                    }
                    .range-buttons {
                        display: flex;
                        flex-wrap: wrap;
                        gap: 10px;
                    }
                    .range-btn {
                        border: 1px solid #e2e8f0;
                        border-radius: 999px;
                        padding: 8px 16px;
                        background: white;
                        cursor: pointer;
                        font-weight: 600;
                        color: #475569;
                        transition: all 0.2s ease;
                    }
                    .range-btn.active {
                        background: #0f172a;
                        color: white;
                        border-color: #0f172a;
                    }
                    .select-wrapper {
                        position: relative;
                        display: flex;
                        align-items: center;
                        border: 1px solid #e2e8f0;
                        border-radius: 12px;
                        padding: 0 12px;
                        color: #94a3b8;
                        gap: 10px;
                    }
                    .select-wrapper select {
                        flex: 1;
                        border: none;
                        padding: 10px 0;
                        font-size: 0.95rem;
                        color: #0f172a;
                        background: transparent;
                        outline: none;
                    }
                    input[type="text"],
                    input[type="date"] {
                        width: 100%;
                        padding: 12px 16px;
                        border: 1px solid #e2e8f0;
                        border-radius: 12px;
                        font-size: 0.95rem;
                        transition: border-color 0.2s ease;
                    }
                    input:focus {
                        border-color: #6366f1;
                        outline: none;
                    }
                    .loading-state {
                        margin: 60px auto;
                        text-align: center;
                        color: #64748b;
                    }
                    .spinner {
                        margin: 0 auto 16px auto;
                        width: 48px;
                        height: 48px;
                        border-radius: 50%;
                        border: 4px solid #e2e8f0;
                        border-top-color: #6366f1;
                        animation: spin 1s linear infinite;
                    }
                    @keyframes spin {
                        to {
                            transform: rotate(360deg);
                        }
                    }
                    .page-header-card {
                        background: white;
                        border-radius: 12px;
                        padding: 18px 20px;
                        box-shadow: 0 8px 30px rgba(15,23,42,0.06);
                        margin-bottom: 18px;
                    }
                    .reports-header {
                        display: flex;
                        justify-content: space-between;
                        align-items: center;
                        gap: 16px;
                    }
                    .metrics-grid {
                        display: grid;
                        grid-template-columns: 1fr;
                        gap: 16px;
                        margin-top: 18px;
                        align-items: stretch;
                        width: 100%;
                    }
                    @media (min-width: 768px) {
                        .metrics-grid {
                            grid-template-columns: repeat(2, 1fr);
                        }
                    }
                    @media (min-width: 992px) {
                        .metrics-grid {
                            grid-template-columns: repeat(3, 1fr);
                        }
                    }
                    @media (min-width: 1200px) {
                        .metrics-grid {
                            grid-template-columns: repeat(4, 1fr);
                        }
                    }
                    .metric-card { min-height: 92px; height: 100%; }

                    .metric-card {
                        background: white;
                        border-radius: 16px;
                        padding: 18px;
                        display: flex;
                        gap: 14px;
                        align-items: center;
                        box-shadow: 0 12px 30px rgba(15, 23, 42, 0.05);
                    }
                    .metric-card.highlight {
                        background: linear-gradient(135deg, #0ea5e9, #6366f1);
                        color: white;
                    }
                    .metric-card.positive {
                        border-bottom: 4px solid #10b981;
                    }
                    .metric-card.negative {
                        border-bottom: 4px solid #ef4444;
                    }
                    .metric-icon {
                        width: 48px;
                        height: 48px;
                        border-radius: 12px;
                        display: flex;
                        align-items: center;
                        justify-content: center;
                        background: #f8fafc;
                        color: #0f172a;
                        font-size: 1.3rem;
                    }
                    .metric-icon.success {
                        background: rgba(16, 185, 129, 0.15);
                        color: #047857;
                    }
                    .metric-icon.warning {
                        background: rgba(251, 191, 36, 0.2);
                        color: #b45309;
                    }
                    .metric-icon.info {
                        background: rgba(59, 130, 246, 0.2);
                        color: #1d4ed8;
                    }
                    .metric-label {
                        margin: 0;
                        color: inherit;
                        font-size: 0.85rem;
                        text-transform: uppercase;
                        letter-spacing: 0.08em;
                    }
                    .metric-value {
                        margin: 6px 0 0 0;
                        font-size: 1.5rem;
                        font-weight: 700;
                        color: inherit;
                    }
                    .metric-subvalue {
                        margin: 4px 0 0 0;
                        font-size: 0.85rem;
                        color: inherit;
                    }
                    .expense-breakdown-card {
                        margin-top: 24px;
                        background: white;
                        border-radius: 16px;
                        padding: 20px;
                        box-shadow: 0 12px 30px rgba(15, 23, 42, 0.05);
                    }
                    .expense-breakdown-card h3 {
                        margin: 0 0 16px 0;
                        font-size: 1.05rem;
                        color: #0f172a;
                    }
                    .expense-grid {
                        display: grid;
                        grid-template-columns: repeat(
                            auto-fit,
                            minmax(200px, 1fr)
                        );
                        gap: 12px;
                    }
                    .expense-item {
                        display: flex;
                        flex-direction: column;
                        padding: 12px;
                        background: #f8fafc;
                        border-radius: 12px;
                    }
                    .expense-label {
                        font-size: 0.85rem;
                        color: #64748b;
                        margin-bottom: 6px;
                    }
                    .expense-value {
                        font-size: 1.1rem;
                        font-weight: 700;
                        color: #0f172a;
                    }
                    .expense-percent {
                        font-size: 0.8rem;
                        color: #94a3b8;
                        margin-top: 2px;
                    }
                    .insights-grid {
                        margin-top: 24px;
                        display: grid;
                        grid-template-columns: 1fr;
                        gap: 16px;
                    }
                    @media (min-width: 768px) {
                        .insights-grid {
                            grid-template-columns: repeat(2, 1fr);
                        }
                    }
                    .insight-card {
                        background: white;
                        border-radius: 16px;
                        padding: 20px;
                        box-shadow: 0 12px 30px rgba(15, 23, 42, 0.05);
                    }
                    .insight-card h3 {
                        margin: 0 0 12px 0;
                        font-size: 1.05rem;
                        color: #0f172a;
                    }
                    .progress {
                        width: 100%;
                        height: 12px;
                        border-radius: 999px;
                        background: #e2e8f0;
                        overflow: hidden;
                        margin-bottom: 10px;
                    }
                    .progress-fill {
                        height: 100%;
                        background: linear-gradient(135deg, #22c55e, #16a34a);
                    }
                    .progress-labels {
                        display: flex;
                        justify-content: space-between;
                        align-items: center;
                        font-weight: 600;
                        font-size: 0.95rem; /* Match input size */
                    }
                    .positive {
                        color: #16a34a;
                    }
                    .negative {
                        color: #dc2626;
                    }
                    .pill-list {
                        list-style: none;
                        margin: 0;
                        padding: 0;
                        display: flex;
                        flex-direction: column;
                        gap: 10px;
                    }
                    .pill-list li {
                        display: flex;
                        justify-content: space-between;
                        align-items: center;
                        padding: 10px 14px;
                        border-radius: 12px;
                        background: #f8fafc;
                        font-weight: 600;
                        font-size: 0.95rem;
                    }
                    .pill-list .role {
                        text-transform: capitalize;
                        color: #334155;
                    }
                    .pill-list .amount {
                        color: #0f172a;
                    }
                    .tables-grid {
                        margin-top: 24px;
                        display: grid;
                        grid-template-columns: 1fr;
                        gap: 16px;
                    }
                    @media (min-width: 768px) {
                        .tables-grid {
                            grid-template-columns: repeat(2, 1fr);
                        }
                    }
                    .table-card {
                        background: white;
                        border-radius: 16px;
                        padding: 20px;
                        box-shadow: 0 12px 30px rgba(15, 23, 42, 0.05);
                    }
                    .table-card.full-width {
                        grid-column: auto;
                    }
                    .table-card.training-card-compact {
                        max-width: 100%;
                        margin: 12px 0;
                    }
                    .training-table-scroll {
                        max-height: 200px; /* shows ~5 rows */
                        overflow-y: auto;
                    }
                    .training-table-scroll::-webkit-scrollbar {
                        width: 6px;
                    }
                    .training-table-scroll::-webkit-scrollbar-track {
                        background: #f1f5f9;
                        border-radius: 10px;
                    }
                    .training-table-scroll::-webkit-scrollbar-thumb {
                        background: #cbd5e1;
                        border-radius: 10px;
                    }
                    .training-table-scroll::-webkit-scrollbar-thumb:hover {
                        background: #94a3b8;
                    }
                    /* sticky header for table inside scrollable container */
                    .training-table-scroll thead th {
                        position: sticky;
                        top: 0;
                        background: white;
                        z-index: 2;
                    }
                    .two-column-row {
                        display: block;
                        margin-top: 12px;
                    }
                    .recent-controls {
                        display: flex;
                        gap: 12px;
                        align-items: center;
                        margin: 8px 0 12px 0;
                    }
                    .recent-controls .select-wrapper {
                        max-width: 220px;
                    }
                    .recent-controls input[type="text"] {
                        padding: 10px 12px;
                        border-radius: 10px;
                        border: 1px solid #e2e8f0;
                        flex: 1;
                    }
                    .table-card-header {
                        display: flex;
                        align-items: center;
                        justify-content: space-between;
                        margin-bottom: 12px;
                    }
                    .table-card-header h3 {
                        margin: 0;
                        font-size: 1.05rem;
                    }
                    .badge {
                        padding: 4px 10px;
                        border-radius: 999px;
                        font-size: 0.85rem;
                        font-weight: 600;
                    }
                    .badge.positive {
                        background: rgba(34, 197, 94, 0.15);
                        color: #15803d;
                    }
                    .badge.negative {
                        background: rgba(248, 113, 113, 0.15);
                        color: #b91c1c;
                    }
                    .badge.info {
                        background: rgba(59, 130, 246, 0.15);
                        color: #1d4ed8;
                    }
                    table {
                        width: 100%;
                        border-collapse: collapse;
                    }
                    thead th {
                        text-align: left;
                        font-size: 0.8rem;
                        text-transform: uppercase;
                        letter-spacing: 0.06em;
                        color: #94a3b8;
                        padding-bottom: 10px;
                    }
                    tbody td {
                        padding: 10px 0;
                        border-top: 1px solid #f1f5f9;
                        color: #0f172a;
                        font-weight: 500;
                        font-size: 0.95rem; /* Match input size */
                        vertical-align: middle;
                    }
                    /* Ensure date columns don't wrap awkwardly */
                    td:last-child {
                        white-space: nowrap;
                    }
                    .table-scroll {
                        width: 100%;
                        overflow-x: auto;
                    }
                    .recent-table-scroll {
                        width: 100%;
                        overflow-y: auto;
                        max-height: 200px; /* Match Training Participants card height */
                    }
                    .recent-table-scroll::-webkit-scrollbar {
                        width: 6px;
                    }
                    .recent-table-scroll::-webkit-scrollbar-track {
                        background: #f1f5f9;
                        border-radius: 10px;
                    }
                    .recent-table-scroll::-webkit-scrollbar-thumb {
                        background: #cbd5e1;
                        border-radius: 10px;
                    }
                    .recent-table-scroll::-webkit-scrollbar-thumb:hover {
                        background: #94a3b8;
                    }
                    .recent-table-scroll thead th {
                        position: sticky;
                        top: 0;
                        background: white;
                        z-index: 2;
                    }
                    .product-name {
                        display: flex;
                        flex-direction: column;
                        font-weight: 600;
                    }
                    .product-name small {
                        color: #94a3b8;
                        font-weight: 500;
                    }
                    .muted-text {
                        color: #94a3b8;
                        font-size: 0.9rem;
                        margin: 0;
                    }
                    .status-badge {
                        padding: 4px 10px;
                        border-radius: 999px;
                        font-size: 0.8rem;
                        font-weight: 600;
                        text-transform: capitalize;
                    }
                    .status-badge.completed {
                        background: rgba(34, 197, 94, 0.2);
                        color: #15803d;
                    }
                    .status-badge.pending {
                        background: rgba(251, 191, 36, 0.2);
                        color: #b45309;
                    }
                    .status-badge.confirmed {
                        background: rgba(59, 130, 246, 0.08);
                        color: #1e40af;
                    }
                    .status-badge.dispatched {
                        background: rgba(139, 92, 246, 0.08);
                        color: #6b21a8;
                    }
                    .status-badge.delivered {
                        background: rgba(16, 185, 129, 0.08);
                        color: #065f46;
                    }
                    .status-badge.cancelled {
                        background: rgba(248, 113, 113, 0.08);
                        color: #b91c1c;
                    }
                    .status-badge.cancelled {
                        background: rgba(248, 113, 113, 0.2);
                        color: #b91c1c;
                    }
                    @media (max-width: 768px) {
                        .reports-header {
                            flex-direction: column;
                        }
                        .header-actions {
                            width: 100%;
                            justify-content: stretch;
                        }
                        .filters-card {
                            padding: 16px;
                        }
                        .filter-row {
                            flex-direction: column;
                        }
                        .filter-group,
                        .filter-group.compact {
                            flex: 1 1 auto;
                        }
                        .range-buttons {
                            flex-direction: column;
                        }
                        .range-btn {
                            width: 100%;
                            text-align: center;
                        }
                        .tables-grid {
                            grid-template-columns: 1fr;
                        }
                        .expense-grid {
                            grid-template-columns: 1fr;
                        }
                    }
                `}</style>
      </div>
    </div>
  );
};

export default Reports;
